import { migrate } from '@measured/puck';

import { config } from '@vb-django/components/editor/EditorConfig';

import {
  Article,
  ArticleItem,
  PostContentApiData,
  PuckData,
  RawPuckComponent,
  StringKeyedObject
} from './PuckHelper.types';

export type {
  Article,
  ArticleItem,
  PuckData,
  RawPuckComponent,
  StringKeyedObject
} from './PuckHelper.types';

const EMBEDDED_SKIP_TYPES = new Set(['Navigation', 'Footer']);

export type PuckPostMetadata = {
  title?: string;
  published_date?: string;
  category_name?: string;
};

function toPuckData(post: unknown): PuckData | null {
  if (!post || typeof post !== 'object') {
    return null;
  }
  const obj = post as StringKeyedObject;
  if (obj.zones !== undefined || (Array.isArray(obj.content) && obj.root !== undefined)) {
    return {
      content: Array.isArray(obj.content) ? (obj.content as RawPuckComponent[]) : [],
      root: obj.root ?? {},
      zones:
        obj.zones && typeof obj.zones === 'object' && !Array.isArray(obj.zones)
          ? (obj.zones as StringKeyedObject)
          : {}
    };
  }
  if (Array.isArray(obj.content)) {
    return { content: obj.content as RawPuckComponent[], root: {}, zones: {} };
  }
  return null;
}

export function articleToPuckMetadata(post: Article | undefined): PuckPostMetadata | undefined {
  if (!post) {
    return undefined;
  }
  return {
    title: post.title,
    published_date: post.published_date,
    category_name: post.category_name
  };
}

/** Prepare content the same way as PuckPageView, minus site chrome (Navigation/Footer). */
export function preparePuckPageContent(
  puckContent: unknown,
  metadata?: PuckPostMetadata
): RawPuckComponent[] {
  const puckData = toPuckData(puckContent);
  if (!puckData) {
    return [];
  }

  if (metadata) {
    const breadcrumbs = metadata.category_name
      ? [{ name: metadata.category_name, slug: '/' }]
      : Window.react_data?.breadcrumbs;

    Window.react_data = {
      ...Window.react_data,
      title: metadata.title ?? Window.react_data?.title,
      published_date: metadata.published_date ?? Window.react_data?.published_date,
      breadcrumbs
    };
  }

  const migrated = migrate({ ...puckData, ...metadata }, config) as PuckData;
  const content = Array.isArray(migrated.content) ? migrated.content : [];

  return content.filter((component) => !EMBEDDED_SKIP_TYPES.has(component.type));
}

export function getPayloadFromPostContentResponse(resp: {
  ok: boolean;
  data?: unknown;
}): Article | undefined {
  if (!resp.ok) {
    return undefined;
  }
  const data = resp.data as PostContentApiData | Article | undefined;
  if (!data || typeof data !== 'object') {
    return undefined;
  }
  const c = (data as PostContentApiData).content;
  const payload = c !== null && typeof c === 'object' && !Array.isArray(c) ? c : (data as Article);
  return payload as Article;
}

export function mapPostToArticle(post: Article): ArticleItem {
  return {
    id: String(post.id),
    date: post.published_date ?? '',
    category: post.category_name ?? '',
    source: post.source ?? '',
    title: post.title ?? '',
    summary: post.description ?? '',
    author: post.author ?? ''
  };
}

export function getPostsFromResponse(resp: {
  ok?: boolean;
  data?: { posts?: Article[] };
}): Article[] {
  if (!resp?.data?.posts || !Array.isArray(resp.data.posts)) {
    return [];
  }
  return resp.data.posts;
}

export function findArticleById(posts: Article[], id: string | null): Article | undefined {
  if (!id) {
    return undefined;
  }
  return posts.find((p) => String(p.id) === id);
}
